import { Component, DestroyRef, ElementRef, inject, ViewChild } from '@angular/core';
import { LoadingService } from '../../services/loading.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DatePipe, NgIf } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { Subscription } from 'rxjs';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { HttpServiceService } from '../../services/http-service.service';
import { Select2, Select2Data } from 'ng-select2-component';
import { provideNativeDateAdapter } from '@angular/material/core';
import { LocalStorageService } from '../../services/local-storage.service';
import Offcanvas from 'bootstrap/js/dist/offcanvas';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-profile',
  imports: [MatDatepickerModule, NgIf, ReactiveFormsModule, MatFormFieldModule, MatRadioModule, FormsModule, Select2, DatePipe],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.css',
  providers: [provideNativeDateAdapter()]
})
export class ProfileComponent {
  private userId: string = '';
  userInfo: any;
  loadingService = inject(LoadingService);
  addressForm!: FormGroup;
  personalForm!: FormGroup;
  private getUserDetailsSubscription?: Subscription;
  private getCountriesSubscription?: Subscription;
  private getStatesSubscription?: Subscription;
  private getCitiesSubscription?: Subscription;
  private updateUserSubscription?: Subscription;
  personal_info: any;
  countries: Select2Data = [];
  states: Select2Data = [];
  cities: Select2Data = [];
  selected_country: string | undefined = '';
  selected_state: string | undefined = '';
  selected_city: string | undefined = '';
  workForm!: FormGroup;
  private authService = inject(LocalStorageService);
  @ViewChild('add_address_popup') addAddressCanvas!: ElementRef;
  private addAddressInstance: Offcanvas | null = null;
  @ViewChild('add_work_popup') addWorkCanvas!: ElementRef;
  private addWorkInstance: Offcanvas | null = null;
  @ViewChild('add_personal_popup') addPersonalCanvas!: ElementRef;
  private addPersonalInstance: Offcanvas | null = null;
  private destroyRef = inject(DestroyRef);

  constructor(private fb: FormBuilder, private alertService: NgxToastrService, private httpService: HttpServiceService) {
    const userInfo: any = this.authService.getItem("userInfo");
    this.userId = userInfo?.id ?? "";

    this.addressForm = this.fb.group({
      address: ['', [Validators.required]],
      country: [null, [Validators.required]],
      city: [null, [Validators.required]],
      state: [null, [Validators.required]],
      postal_code: ['', [Validators.required]]
    });

    this.workForm = this.fb.group({
      company_name: ['', [Validators.required]],
      company_address: ['', [Validators.required]],
      company_phone: [''],
      company_email: [''],
      designation: [''],
      department: [''],
      work_start_date: [''],
      work_end_date: [''],
      work_experience: [''],
      work_location: [''],
      monthly_income: ['']
    });

    this.personalForm = this.fb.group({
      gender: [''],
      phone: ['', [Validators.required]],
      alternate_phone: [''],
      alternate_email: [''],
      dob: [''],
      marital_status: [''],
      pan: [''],
      aadhar: [''],
      driving_license: ['']
    });
  }

  ngOnInit() {
    this.getUserDetailsById();

    this.addressForm.get('country')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      const selected = this.countries.find((opt: any) => opt.value == id);
      this.selected_country = selected?.label;
    });

    this.addressForm.get('state')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      const selected = this.states.find((opt: any) => opt.value == id);
      this.selected_state = selected?.label;
    });

    this.addressForm.get('city')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      const selected = this.cities.find((opt: any) => opt.value == id);
      this.selected_city = selected?.label;
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const addressCanvasElement = this.addAddressCanvas?.nativeElement;
      if (addressCanvasElement) {
        this.addAddressInstance = Offcanvas.getOrCreateInstance(addressCanvasElement, {
          backdrop: 'static',
          keyboard: false,
        });
      }

      const workCanvasElement = this.addWorkCanvas?.nativeElement;
      if (workCanvasElement) {
        this.addWorkInstance = Offcanvas.getOrCreateInstance(workCanvasElement, {
          backdrop: 'static',
          keyboard: false,
        });
      }

      const personalCanvasElement = this.addPersonalCanvas?.nativeElement;
      if (personalCanvasElement) {
        this.addPersonalInstance = Offcanvas.getOrCreateInstance(personalCanvasElement, {
          backdrop: 'static',
          keyboard: false,
        });
      }
    });
  }

  getUserDetailsById() {
    this.getUserDetailsSubscription = this.httpService.getUser(this.userId).subscribe({
      next: (response) => {
        this.userInfo = response.data;
        this.personal_info = response.data?.personal_info ?? {};
      }
    })
  }

  handleImageError(event: any) {
    event.target.onerror = null;
    event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRTVFN0VCIi8+CjxwYXRoIGQ9Ik01MCA1MEMyOC43ODk1IDUwIDI1IDM2LjIxMDUgMjUgMzVDMjUgMjMuNzg5NSAzMy43ODk1IDE1IDQ1IDE1QzU2LjIxMDUgMTUgNjUgMjMuNzg5NSA2NSAzNUM2NSAzNi4yMTA1IDYxLjIxMDUgNTAgNTAgNTBaIiBmaWxsPSIjOUM5Qzk3Ii8+CjxwYXRoIGQ9Ik01MCA1NUMyOC43ODk1IDU1IDE1IDY4Ljc4OTUgMTUgODBWOTVIODVWODBDODUgNjguNzg5NSA3MS4yMTA1IDU1IDUwIDU1WiIgZmlsbD0iIzlDOUM5NyIvPgo8L3N2Zz4K';
  }

  updateCoverPhoto(event: any) {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.files && inputElement.files[0]) {
      const file = inputElement.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        if (this.userInfo) {
          this.userInfo.profile_image = reader.result as string;
        }
      };
      reader.readAsDataURL(file);

      // Save the image to the server using FormData
      const formData = new FormData();
      formData.append('profile_image', file);

      this.updateUserSubscription = this.httpService.editUser(formData, this.userId).subscribe({
        next: (response) => {
          this.alertService.success(response.message, "toast-top-right");
          this.getUserDetailsById();
        }
      });
    }
  }

  togglePersonal() {
    this.personalForm.patchValue(this.userInfo?.personal_info);
    if (this.addPersonalInstance) {
      this.addPersonalInstance.show();
    }
  }

  personalCancel() {
    this.personalForm.reset();
    if (this.addPersonalInstance) {
      this.addPersonalInstance.hide();
    }
  }

  onPersonalSubmit() {
    if (this.personalForm.valid) {
      const personalUpdateObj = Object.fromEntries(
        Object.entries(this.personalForm.value).filter(([_, v]) => v !== null && v !== '')
      ) as unknown as any;
      this.updateUserSubscription = this.httpService.editUser(personalUpdateObj, this.userId).subscribe({
        next: (response) => {
          this.personalCancel();
          this.getUserDetailsById();
          this.alertService.success(response.message, "toast-top-right");
        }
      });
    }
  }

  toggleAddress() {
    this.getCountries();
    this.addressForm.patchValue(this.userInfo?.address_info);
    if (this.addAddressInstance) {
      this.addAddressInstance.show();
    }
  }

  addressCancel() {
    this.addressForm.reset();
    if (this.addAddressInstance) {
      this.addAddressInstance.hide();
    }
  }

  onAddressSubmit() {
    if (this.addressForm.valid) {
      let addressObj: any = {};
      Object.keys(this.addressForm.controls).forEach((key) => {
        let value = this.addressForm.get(key)?.value;
        if (key == 'country') {
          value = this.selected_country ?? "";
        } else if (key == 'state') {
          value = this.selected_state ?? "";
        } else if (key == 'city') {
          value = this.selected_city ?? "";
        }
        if (value) addressObj[key] = value;
      });
      this.updateUserSubscription = this.httpService.editUser(addressObj, this.userId).subscribe({
        next: (response) => {
          this.addressCancel();
          this.getUserDetailsById();
          this.alertService.success(response.message, "toast-top-right");
        }
      })
    }
  }

  getCountries() {
    this.getCountriesSubscription = this.httpService.getLocations('country').subscribe({
      next: (resp) => {
        let country = resp.data?.find((c: { name: any; }) => c.name == this.userInfo?.address_info?.country);
        for (let country of resp.data) {
          this.countries.push(
            {
              value: country.id,
              label: country.name,
              data: { name: country.name }
            }
          )
        }
        this.addressForm?.patchValue({
          country: country?.id
        });
        this.getStates();
      },
      error: (error) => {

      }
    });
  }

  getStates() {
    let countryId = this.addressForm?.get('country')?.value;
    if (countryId) {
      this.states = [];
      this.getStatesSubscription = this.httpService.getLocations('state', '&countryId=' + countryId).subscribe({
        next: (resp) => {
          let state = resp.data?.find((c: { name: any; }) => c.name == this.userInfo?.address_info?.state);
          for (let state of resp.data) {
            this.states.push(
              {
                value: state.id,
                label: state.name,
                data: { name: state.name }
              }
            )
          }
          this.addressForm?.patchValue({
            state: state?.id
          });
          this.getCities();
        },
        error: (error) => {

        }
      });
    }
  }

  getCities() {
    let stateId = this.addressForm?.get('state')?.value;
    if (stateId) {
      this.cities = [];
      this.getCitiesSubscription = this.httpService.getLocations('city', '&stateId=' + stateId).subscribe({
        next: (resp) => {
          let city = resp.data?.find((c: { name: any; }) => c.name == this.userInfo?.address_info?.city);
          for (let city of resp.data) {
            this.cities.push(
              {
                value: city.id,
                label: city.name,
                data: { name: city.name }
              }
            )
          }
          this.addressForm?.patchValue({
            city: city?.id
          });
        },
        error: (error) => {

        }
      });
    }
  }

  toggleWork() {
    this.workForm.patchValue(this.userInfo?.work_info);
    if (this.addWorkInstance) {
      this.addWorkInstance.show();
    }
  }

  workCancel() {
    this.workForm.reset();
    if (this.addWorkInstance) {
      this.addWorkInstance.hide();
    }
  }

  onWorkSubmit() {
    if (this.workForm.valid) {
      const workUpdateObj = Object.fromEntries(
        Object.entries(this.workForm.value).filter(([_, v]) => v !== null && v !== '')
      ) as unknown as any;
      this.updateUserSubscription = this.httpService.editUser(workUpdateObj, this.userId).subscribe({
        next: (response) => {
          this.workCancel();
          this.getUserDetailsById();
          this.alertService.success(response.message, "toast-top-right");
        }
      });
    }
  }

  ngOnDestroy() {
    this.getUserDetailsSubscription?.unsubscribe();
    this.getCountriesSubscription?.unsubscribe();
    this.getStatesSubscription?.unsubscribe();
    this.getCitiesSubscription?.unsubscribe();
    this.updateUserSubscription?.unsubscribe();
  }
}
